import React, { memo, useMemo } from "react";
import {
  Box,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Button,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { tokens, withAlpha } from "../../Common/designTokens";
import FilterChips from "../InventoryComponents/FilterChips";
import {
  parseCharacteristics,
  preprocessFilters,
} from "../OrderComponents/columnConfigUtils";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "min(900px, 90vw)",
  maxHeight: "80vh",
  backgroundColor: tokens.colors.bodyBackgroundColor,
  border: `1px solid ${tokens.colors.bodyBorderColor}`,
  borderRadius: "8px",
  boxShadow: 24,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

/**
 * Renders a rung's stored filters exactly as the Bond Characteristic tab's Characteristics
 * column does — same parse, same preprocess, same read-only compact chips — so the two
 * screens always show identical content for the same characteristic.
 */
const PendingCharacteristicChips = memo(({ characteristic }) => {
  const processedFilters = useMemo(
    () => preprocessFilters(parseCharacteristics(characteristic?.characteristics)),
    [characteristic?.characteristics]
  );

  if (!processedFilters || Object.keys(processedFilters).length === 0) return "---";

  return (
    <FilterChips input={processedFilters} maxVisibleChips={2} readOnly compact />
  );
});

/**
 * Rung picker shown when a ladder has more than one PENDING characteristic.
 * Each row is one rung awaiting a replacement bond; choosing one runs the same
 * view-inventory flow the Bond Characteristic tab uses.
 */
const LadderPendingCharacteristicsModal = ({
  open,
  onClose,
  ladderName,
  rows = [],
  onSelect,
}) => (
  <Modal open={open} onClose={onClose}>
    <Box sx={modalStyle}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "12px 16px",
          borderBottom: `1px solid ${tokens.colors.bodyBorderColor50}`,
          flexShrink: 0,
        }}
      >
        <Typography variant="h6" sx={{ color: tokens.colors.fontColour, fontWeight: "bold" }}>
          {ladderName || "Pending Characteristics"}
        </Typography>
        <IconButton size="small" onClick={onClose} sx={{ color: tokens.colors.fontColour }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ padding: "8px 16px", flexShrink: 0 }}>
        <Typography variant="body2" sx={{ color: tokens.colors.fontColour90 }}>
          {rows.length} rungs are waiting for a replacement bond. Pick one to view matching
          inventory.
        </Typography>
      </Box>

      <Box sx={{ overflow: "auto", flex: 1, pb: 3 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {["Characteristics", "Reason", ""].map((heading, index) => (
                <TableCell
                  key={heading || `action-${index}`}
                  sx={{
                    color: tokens.colors.fontColour,
                    fontWeight: "bold",
                    backgroundColor: tokens.colors.bodyBackgroundColor,
                    borderBottom: `1px solid ${tokens.colors.bodyBorderColor50}`,
                  }}
                >
                  {heading}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              const characteristicId = row?.characteristic?.characteristicId;
              return (
                <TableRow
                  key={characteristicId ?? `${row?.rungNo}-${row?.rungId}`}
                  sx={{ "&:hover": { backgroundColor: tokens.colors.defaultGridHoverColor } }}
                >
                  <TableCell
                    sx={{ color: tokens.colors.fontColour, verticalAlign: "middle" }}
                  >
                    <PendingCharacteristicChips characteristic={row?.characteristic} />
                  </TableCell>
                  <TableCell sx={{ color: tokens.colors.fontColour }}>
                    {row?.characteristic?.reason || "---"}
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      onClick={() => onSelect(row)}
                      sx={{
                        textTransform: "none",
                        fontWeight: 700,
                        borderRadius: "8px",
                        border: `1px solid ${tokens.palette.primary}`,
                        color: tokens.palette.primary,
                        whiteSpace: "nowrap",
                        "&:hover": {
                          backgroundColor: withAlpha(tokens.palette.primary, 0.1),
                        },
                      }}
                    >
                      View Inventory
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    </Box>
  </Modal>
);

export default LadderPendingCharacteristicsModal;
